import { Component, OnInit, inject } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { CreatorConnection, CreatorLibraryService } from '../../services/creator-library.service';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';

type Scope = 'personal' | 'tenant';

/** Error codes of the connect route (al-filemanager creator_connection_ops) → message. */
const ERRORS: Record<string, string> = {
  figma_unauthorized: 'token',
  rate_limited: 'rateLimited',
  upstream_failed: 'figma',
  bad_request: 'format',
  forbidden: 'forbidden',
};

/**
 * Einstellungen › Verbindungen: Figma for the course editor (ADR 0013 at
 * AlphaCourse; al-filemanager `/creator/connections`). Everyone with Creator
 * access connects their own Figma account with a personal access token; an
 * admin may set one for the whole organisation, which applies to everyone
 * without their own. The token goes in once and is never shown again — Figma
 * checks it before it is stored. 404/403/501 = Figma is not offered here.
 */
@Component({
  selector: 'app-creator-connections-panel',
  templateUrl: './creator-connections-panel.component.html',
  styleUrls: ['./creator-connections-panel.component.scss'],
})
export class CreatorConnectionsPanelComponent implements OnInit {
  private readonly library = inject(CreatorLibraryService);
  private readonly dialog = inject(NbDialogService);
  private readonly toastr = inject(NbToastrService);
  private readonly translate = inject(TranslateService);

  readonly scopes: Scope[] = ['personal', 'tenant'];
  loading = true;
  available = true;
  canManageTenant = false;
  connection: Record<Scope, CreatorConnection | null> = { personal: null, tenant: null };
  token: Record<Scope, string> = { personal: '', tenant: '' };
  busy: Record<Scope, boolean> = { personal: false, tenant: false };
  error: Record<Scope, string | null> = { personal: null, tenant: null };

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.library
      .connections()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          this.connection = { personal: res.figma.personal, tenant: res.figma.tenant };
          this.canManageTenant = res.figma.canManageTenant;
          this.available = true;
        },
        error: (err) => {
          if ([403, 404, 501].includes(err?.status)) {
            this.available = false;
          } else {
            this.toastr.danger(this.translate.instant('pages.settings.connections.errors.load'), '');
          }
        },
      });
  }

  /** The form shows while there is nothing readable to show. */
  showForm(scope: Scope): boolean {
    const found = this.connection[scope];
    return (!found || found.status !== 'connected') && (scope === 'personal' || this.canManageTenant);
  }

  connect(scope: Scope): void {
    const token = this.token[scope].trim();
    this.error[scope] = null;
    if (!token) {
      this.error[scope] = 'pages.settings.connections.errors.empty';
      return;
    }
    this.busy[scope] = true;
    this.library
      .connectFigma(token, scope === 'tenant')
      .pipe(finalize(() => (this.busy[scope] = false)))
      .subscribe({
        next: (found) => {
          this.connection[scope] = found;
          this.token[scope] = '';
          this.toastr.success(this.translate.instant('pages.settings.connections.connected'), '');
        },
        error: (err) => {
          const code = err?.error?.error as string | undefined;
          this.error[scope] = `pages.settings.connections.errors.${ERRORS[code ?? ''] ?? 'failed'}`;
        },
      });
  }

  disconnect(scope: Scope): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        context: {
          title: 'pages.settings.connections.disconnectDialog.title',
          message: this.translate.instant(`pages.settings.connections.disconnectDialog.${scope}`),
          confirmLabel: 'pages.settings.connections.disconnect',
          cancelLabel: 'buttons.cancel',
          confirmStatus: 'primary',
          cancelStatus: 'basic',
        },
        hasBackdrop: true,
      })
      .onClose.subscribe((confirmed) => {
        if (!confirmed) return;
        this.busy[scope] = true;
        this.library
          .disconnectFigma(scope === 'tenant')
          .pipe(finalize(() => (this.busy[scope] = false)))
          .subscribe({
            next: () => {
              this.connection[scope] = null;
              this.toastr.success(this.translate.instant('pages.settings.connections.disconnected'), '');
            },
            error: () => this.toastr.danger(this.translate.instant('pages.settings.connections.errors.disconnect'), ''),
          });
      });
  }

  /** „@handle · email" — whatever Figma told us when connecting. */
  account(found: CreatorConnection): string {
    const parts = [found.account?.handle, found.account?.email].filter(Boolean);
    return parts.join(' · ');
  }
}
